import { useState } from 'react';
import {
  Box,
  Flex,
  Select,
  Checkbox,
  Heading,
  VStack,
  Text,
  Spinner,
  Accordion,
  HStack,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { useL10ActionItems, ActionItemFilters } from '../useL10ActionItems';
import { L10ActionItemListItem } from './L10ActionItemListItem';
import { L10AddActionItemModal } from './L10AddActionItemModal';
import { L10EditActionItemModal } from './L10EditActionItemModal';
import { L10DropActionItemModal } from './L10DropActionItemModal';
import { User, Rock, Issue, Metric, ActionItem } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';

interface L10ActionItemSectionProps {
  users: User[];
  rocks: Rock[];
  issues: Issue[];
  metrics: Metric[];
  meetingId?: string;
  onAddToNotes?: (actionItem: ActionItem) => void;
}

/**
 * L10 Action Item Section Component
 * Main component for displaying and managing action items in the L10 Meeting page
 * Includes filtering, real-time updates, and CRUD operations
 */
export function L10ActionItemSection({
  users,
  rocks,
  issues,
  metrics,
  meetingId,
  onAddToNotes,
}: L10ActionItemSectionProps) {
  const { currentUser } = useAuth();
  const [filters, setFilters] = useState<ActionItemFilters>({
    complete: false,
    dropped: false,
    overdueOnly: true, // Default to overdue as in screenshot
    ownerId: 'all',
    linkType: 'all',
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingActionItem, setEditingActionItem] = useState<ActionItem | null>(null);
  const [droppingActionItem, setDroppingActionItem] = useState<ActionItem | null>(null);

  const { actionItems, loading, createActionItem, updateActionItem, completeActionItem, uncompleteActionItem, dropActionItem } =
    useL10ActionItems(filters);

  // Separate overdue from others for display
  const overdueActionItems = actionItems.filter((t) => {
    const dueDate = new Date(t.dueDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < now && !t.complete;
  });
  const otherActionItems = actionItems.filter((t) => {
    const dueDate = new Date(t.dueDate);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate >= now || t.complete;
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleCreateActionItem = async (formData: any) => {
    await createActionItem({
      ...formData,
      createdByUserId: currentUser?.uid,
      createdInMeetingId: meetingId,
    });
    setShowAddModal(false);
  };

  const handleUpdateActionItem = async (formData: any) => {
    if (!editingActionItem) return;
    await updateActionItem(editingActionItem.id, formData);
    setEditingActionItem(null);
  };

  const handleCompleteActionItem = async (actionItem: ActionItem) => {
    if (actionItem.complete) {
      await uncompleteActionItem(actionItem.id);
    } else {
      await completeActionItem(actionItem.id);
    }
  };

  const handleDropActionItem = async (reason?: string) => {
    if (!droppingActionItem) return;
    await dropActionItem(droppingActionItem.id, reason);
    setDroppingActionItem(null);
  };

  return (
    <Box>
      <Flex justify="flex-end" align="center" mb={4} px={4}>
        <Button size="sm" colorScheme="blue" onClick={() => setShowAddModal(true)}>
          Add Action Item
        </Button>
      </Flex>

      <HStack spacing={4} mb={4} px={4}>
        <Select
          size="sm"
          value={filters.ownerId}
          onChange={(e) => setFilters((f) => ({ ...f, ownerId: e.target.value }))}
          maxW="200px"
        >
          <option value="all">All Users</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </Select>
        <Select
          size="sm"
          value={filters.linkType}
          onChange={(e) =>
            setFilters((f) => ({ ...f, linkType: e.target.value as ActionItemFilters['linkType'] }))
          }
          maxW="200px"
        >
          <option value="all">All Types</option>
          <option value="issue">Issue</option>
          <option value="metric">Metric</option>
          <option value="standalone">Standalone</option>
        </Select>
        <Checkbox
          isChecked={filters.overdueOnly}
          onChange={(e) => setFilters((f) => ({ ...f, overdueOnly: e.target.checked }))}
        >
          Overdue Only
        </Checkbox>
      </HStack>

      <VStack spacing={3} align="stretch" px={4}>
        {loading && (
          <Box textAlign="center" py={4}>
            <Spinner />
          </Box>
        )}
        {!loading && overdueActionItems.length > 0 && (
          <Text fontSize="sm" color="red.500" fontWeight="bold">
            {overdueActionItems.length} overdue item{overdueActionItems.length !== 1 ? 's' : ''}
          </Text>
        )}

        {/* Use Chakra's Accordion for expand/collapse */}
        <Accordion allowMultiple defaultIndex={[]}>
          {actionItems.map((actionItem) => (
            <L10ActionItemListItem
              key={actionItem.id}
              actionItem={actionItem}
              users={users}
              rocks={rocks}
              issues={issues}
              metrics={metrics}
              onComplete={() => handleCompleteActionItem(actionItem)}
              onEdit={() => setEditingActionItem(actionItem)}
              onDrop={() => setDroppingActionItem(actionItem)}
              onAddToNotes={onAddToNotes ? () => onAddToNotes(actionItem) : undefined}
            />
          ))}
        </Accordion>

        {!loading && actionItems.length === 0 && (
          <Text color="gray.500" textAlign="center" py={4}>
            No action items found for these filters.
          </Text>
        )}
      </VStack>

      {/* Modals */}
      <L10AddActionItemModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleCreateActionItem}
        users={users}
        rocks={rocks}
        issues={issues}
        metrics={metrics}
        currentUserId={currentUser?.uid}
      />

      <L10EditActionItemModal
        isOpen={!!editingActionItem}
        onClose={() => setEditingActionItem(null)}
        onSave={handleUpdateActionItem}
        editingActionItem={editingActionItem}
        users={users}
        rocks={rocks}
        issues={issues}
        metrics={metrics}
      />

      <L10DropActionItemModal
        isOpen={!!droppingActionItem}
        onClose={() => setDroppingActionItem(null)}
        onSave={handleDropActionItem}
        actionItem={droppingActionItem}
      />
    </Box>
  );
}
